// src/components/shop/CheckoutModal.jsx
//
// Checkout page
//
// Steps:
//   1. Delivery details
//   2. Payment
//   3. Complete
//
// Props
//   items       array of cart items (or use the older cartItems / product props)
//   onClose     called when the customer dismisses checkout before ordering
//   onSuccess   called once the backend has accepted/confirmed the order
//   onContinue  called from the "Continue shopping" button on the final screen
//
// The backend (POST CHECKOUT_ENDPOINT) must:
//   - re-price every item from Shopify (never trust the prices sent here),
//   - resolve the Shopify variant from id + selectedSize + selectedColor,
//   - check stock, create the order, and start payment.
// It may answer with { order_id | order_number | id, message?, checkout_url? }.
// If checkout_url is returned for card/bank, the customer is sent there to pay.

import React, { useEffect, useRef, useState } from "react";
import { normalizeItem } from "../../context/CartContext";
import {
  CHECKOUT_ENDPOINT,
  orderStatusEndpoint,
  SHIPPING_FEE,
  T,
  F,
  formatKsh,
} from "../../config/shop";

const STEPS = ["Cart", "Delivery", "Payment", "Done"];

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000;

// ── Helpers ─────────────────────────────────────────────────────────────────

// "0712 345 678"
// "+254712345678"
// "712345678"
// → "254712345678" or null
function normalizeKenyanPhone(input) {
  const cleaned = String(input || "")
    .replace(/[^\d+]/g, "")
    .replace(/^\+/, "");

  const match = cleaned.match(/^(?:254|0)?([17]\d{8})$/);

  return match ? `254${match[1]}` : null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Icons ───────────────────────────────────────────────────────────────────

function IconClose(props) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" {...props}>
      <path
        d="M1.5 1.5l9 9M10.5 1.5l-9 9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCheck(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...props}>
      <path
        d="M2.5 7.2l3 3.1 6-6.6"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCross(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" {...props}>
      <path
        d="M4 4l14 14M18 4L4 18"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconMpesa(props) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" {...props}>
      <rect
        x="5.5"
        y="1.5"
        width="11"
        height="19"
        rx="2.2"
        stroke="#12833B"
        strokeWidth="1.6"
        fill="#fff"
      />
      <circle cx="11" cy="16.4" r="1.15" fill="#12833B" />
      <path
        d="M8 6.5h6M8 9h4.2"
        stroke="#12833B"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCard(props) {
  return (
    <svg width="24" height="22" viewBox="0 0 24 22" fill="none" {...props}>
      <rect
        x="1"
        y="3.5"
        width="22"
        height="15"
        rx="2.4"
        stroke={T.ink}
        strokeWidth="1.6"
        fill="#fff"
      />
      <path d="M1 8.2h22" stroke={T.ink} strokeWidth="1.6" />
      <rect
        x="4"
        y="12"
        width="6"
        height="2.4"
        rx="0.8"
        fill={T.ink}
      />
    </svg>
  );
}

function IconBank(props) {
  return (
    <svg width="24" height="22" viewBox="0 0 24 22" fill="none" {...props}>
      <path
        d="M12 1.6l10.5 5.6H1.5L12 1.6z"
        stroke={T.ink}
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="#fff"
      />
      <path
        d="M3 8.7v9M8 8.7v9M16 8.7v9M21 8.7v9"
        stroke={T.ink}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M1.5 20.4h21"
        stroke={T.ink}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────

export default function CheckoutModal({
  items: itemsProp,
  cartItems,
  product,
  isCart,
  onClose,
  onSuccess,
  onContinue,
}) {
  const rawItems =
    itemsProp ||
    (isCart ? cartItems : product ? [product] : []) ||
    [];

  const items = rawItems.map(normalizeItem).filter(Boolean);

  const productTotal = items.reduce(
    (sum, i) => sum + i.price * i.qty,
    0
  );

  const paymentTotal = productTotal + SHIPPING_FEE;

  const [step, setStep] = useState(1);

  const [shipping, setShipping] = useState({
    fullName: "",
    phone: "",
    email: "",
    county: "",
    town: "",
    address: "",
  });

  const [payMethod, setPayMethod] = useState("mpesa");
  const [mpesaPhone, setMpesaPhone] = useState("");

  const [errors, setErrors] = useState({});
  const [placing, setPlacing] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [result, setResult] = useState({
    ref: "",
    message: "",
  });

  // "sent"  → STK push sent, waiting for PIN
  // "paid"  → confirmed by backend
  // "failed" → declined, timed out, or cancelled
  const [payState, setPayState] = useState(null);

  const pollTimer = useRef(null);
  const pollDeadline = useRef(0);

  // ── Polling ───────────────────────────────────────────────────────────────

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
    }

    pollTimer.current = null;
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  // ── Close / navigation ───────────────────────────────────────────────────

  const closeNow = () => {
    // Don't allow checkout to be closed while M-Pesa payment is in progress.
    if (placing || payState === "sent") {
      return;
    }

    stopPolling();

    if (step === 3) {
      (onContinue || onClose)?.();
    } else {
      onClose?.();
    }
  };

  // ── Order status polling ─────────────────────────────────────────────────

  async function pollOrderStatus(shopifyOrderId) {
    pollDeadline.current = Date.now() + POLL_TIMEOUT_MS;

    const tick = async () => {
      if (Date.now() > pollDeadline.current) {
        setPayState("failed");

        setSubmitError(
          "We didn't get confirmation of your M-Pesa payment in time. If you were charged, contact us with your order number — otherwise please try again."
        );

        return;
      }

      try {
        const res = await fetch(
          orderStatusEndpoint(shopifyOrderId),
          {
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (res.ok) {
          const data = await res.json();

          if (data.status === "paid") {
            setPayState("paid");
            onSuccess?.();
            return;
          }

          if (data.status === "failed") {
            setPayState("failed");

            setSubmitError(
              "The M-Pesa payment was not completed, so your order was cancelled. You have not been charged."
            );

            return;
          }
        }
      } catch (error) {
        // Temporary network error.
        // Continue polling until the deadline.
      }

      pollTimer.current = setTimeout(
        tick,
        POLL_INTERVAL_MS
      );
    };

    tick();
  }

  // ── Validation ────────────────────────────────────────────────────────────

  function validateShipping() {
    const e = {};

    if (!shipping.fullName.trim()) {
      e.fullName = "Enter your full name";
    }

    if (!shipping.phone.trim()) {
      e.phone = "Enter your phone number";
    } else if (!normalizeKenyanPhone(shipping.phone)) {
      e.phone =
        "Enter a valid Kenyan number, e.g. 0712 345 678";
    }

    if (
      shipping.email.trim() &&
      !EMAIL_RE.test(shipping.email.trim())
    ) {
      e.email = "Enter a valid email address";
    }

    if (!shipping.county.trim()) {
      e.county = "Enter your county";
    }

    if (!shipping.town.trim()) {
      e.town = "Enter your town or area";
    }

    if (!shipping.address.trim()) {
      e.address = "Enter your street address";
    }

    return e;
  }

  function validatePayment() {
    const e = {};

    if (payMethod === "mpesa") {
      if (!mpesaPhone.trim()) {
        e.mpesaPhone = "Enter your M-Pesa number";
      } else if (!normalizeKenyanPhone(mpesaPhone)) {
        e.mpesaPhone =
          "Enter a valid Safaricom number, e.g. 0712 345 678";
      }
    }

    return e;
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleShippingNext() {
    const e = validateShipping();

    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setErrors({});

    if (!mpesaPhone.trim()) {
      setMpesaPhone(shipping.phone);
    }

    setStep(2);

    // Move the user back to the top of the checkout page.
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  async function handlePlaceOrder() {
    if (placing) {
      return;
    }

    const e = validatePayment();

    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }

    setErrors({});
    setSubmitError("");
    setPlacing(true);

    const payload = {
      shipping: {
        ...shipping,
        phone:
          normalizeKenyanPhone(shipping.phone) ||
          shipping.phone,
      },

      payment_method: payMethod,

      mpesa_phone:
        payMethod === "mpesa"
          ? normalizeKenyanPhone(mpesaPhone)
          : undefined,

      items: items.map((i) => ({
        id: i.id,
        title: i.title,

        // Display only.
        // Backend MUST re-price from Shopify.
        price: i.price,

        qty: i.qty,
        selectedSize: i.selectedSize,
        selectedColor: i.selectedColor,
      })),

      product_total: productTotal,
      shipping_fee: SHIPPING_FEE,
      payment_total: paymentTotal,
    };

    try {
      const res = await fetch(CHECKOUT_ENDPOINT, {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },

        body: JSON.stringify(payload),
      });

      let data = {};

      try {
        data = await res.json();
      } catch (parseError) {
        // Empty or non-JSON response.
      }

      if (!res.ok) {
        throw new Error(
          data.detail ||
            data.error ||
            data.message ||
            `The order could not be placed (error ${res.status}).`
        );
      }

      const ref =
        data.order_id ||
        data.order_number ||
        data.reference ||
        data.id ||
        "";

      const payUrl =
        data.checkout_url ||
        data.payment_url ||
        data.redirect_url ||
        "";

      const shopifyOrderId =
        data.shopify_order_id;

      // ── Card / bank ───────────────────────────────────────────────────────

      if (payUrl && payMethod !== "mpesa") {
        onSuccess?.();

        window.location.assign(payUrl);

        return;
      }

      // ── M-Pesa ────────────────────────────────────────────────────────────

      setResult({
        ref: String(ref || ""),
        message: data.message || "",
      });

      setStep(3);
      setPayState("sent");

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      if (shopifyOrderId) {
        pollOrderStatus(shopifyOrderId);
      } else {
        // No order ID to check.
        // Nothing else can be verified from the frontend.
        setPayState("paid");
        onSuccess?.();
      }
    } catch (error) {
      console.error("Order failed:", error);

      setSubmitError(
        error instanceof TypeError
          ? "We couldn't reach the server. Check your connection and try again. You have not been charged."
          : error.message ||
              "Something went wrong. Please try again."
      );
    } finally {
      setPlacing(false);
    }
  }

  const setField = (key, value) => {
    setShipping((s) => ({
      ...s,
      [key]: value,
    }));
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes co_spin {
          to {
            transform: rotate(360deg);
          }
        }

        .co-page {
          width: 100%;
          min-height: 100vh;
          background: ${T.surface};
        }

        .co-page-inner {
          width: 100%;
          max-width: 940px;
          margin: 0 auto;
          padding: 24px 20px 48px;
          box-sizing: border-box;
        }

        .co-input {
          width: 100%;
          box-sizing: border-box;
          padding: 10px 12px;
          border: 1.5px solid ${T.border};
          border-radius: 3px;
          font-size: 13.5px;
          font-family: inherit;
          color: ${T.ink};
          background: ${T.white};
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }

        .co-input:focus {
          border-color: ${T.black};
          box-shadow: 0 0 0 2px ${T.yellow};
        }

        .co-input.error {
          border-color: ${T.error};
        }

        .co-input::placeholder {
          color: ${T.faint};
        }

        .co-pay-card {
          border: 2px solid ${T.border};
          border-radius: 4px;
          padding: 12px 14px;
          cursor: pointer;
          transition: all .15s;
          display: flex;
          align-items: center;
          gap: 12px;
          background: ${T.white};
          width: 100%;
          text-align: left;
          font-family: inherit;
        }

        .co-pay-card:hover {
          border-color: ${T.black};
        }

        .co-pay-card.selected {
          border-color: ${T.black};
          background: #fffde0;
        }

        .co-primary:hover:not(:disabled) {
          background: #222 !important;
        }

        .co-primary:disabled {
          opacity: .7;
          cursor: not-allowed;
        }

        .co-link:hover {
          text-decoration: underline;
        }

        .co-checkout-layout {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 320px;
          gap: 24px;
          align-items: start;
        }

        .co-left-column {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .co-right-column {
          min-width: 0;
        }

        @media (max-width: 760px) {
          .co-checkout-layout {
            grid-template-columns: 1fr;
          }

          .co-right-column {
            order: 2;
          }

          .co-left-column {
            order: 1;
          }
        }

        @media (max-width: 560px) {
          .co-page-inner {
            padding: 16px 12px 36px;
          }

          .co-grid {
            grid-template-columns: 1fr !important;
          }

          .co-grid > * {
            grid-column: 1 / -1 !important;
          }

          .co-header {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          .co-stepbar {
            padding-left: 12px !important;
            padding-right: 12px !important;
            overflow-x: hidden;
          }

          .co-stepbar-inner {
            max-width: 100% !important;
          }
        }
      `}</style>

      {/* ── Checkout Page ─────────────────────────────────────────────────── */}

      <div
        className="co-page"
        role="main"
        aria-label="Checkout"
      >
        {/* Header */}

        <div
          className="co-header"
          style={{
            background: T.black,
            borderBottom: `3px solid ${T.yellow}`,
            padding: "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              color: T.white,
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            Checkout
          </span>

          <button
            type="button"
            aria-label="Close checkout"
            onClick={closeNow}
            disabled={placing || payState === "sent"}
            style={{
              background: "rgba(255,255,255,.14)",
              border: "none",
              color: T.white,
              width: 32,
              height: 32,
              borderRadius: "50%",
              cursor:
                placing || payState === "sent"
                  ? "not-allowed"
                  : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconClose />
          </button>
        </div>

        {/* Steps */}

        <StepBar current={step} />

        {/* Page content */}

        <div className="co-page-inner">
          <div className="co-checkout-layout">
            {/* LEFT */}

            <div className="co-left-column">
              {/* ── Step 1: Delivery ─────────────────────────────────────── */}

              {step === 1 && (
                <Section title="Delivery details">
                  <ShippingForm
                    values={shipping}
                    onChange={setField}
                    errors={errors}
                  />

                  <div style={{ marginTop: 16 }}>
                    <PrimaryBtn
                      onClick={handleShippingNext}
                    >
                      Continue to payment
                    </PrimaryBtn>
                  </div>
                </Section>
              )}

              {/* ── Step 2: Payment ──────────────────────────────────────── */}

              {step === 2 && (
                <>
                  <Section
                    title="Delivery details"
                    action={
                      <button
                        type="button"
                        className="co-link"
                        onClick={() => setStep(1)}
                        style={linkButton}
                      >
                        Edit
                      </button>
                    }
                  >
                    <ShippingSummary data={shipping} />
                  </Section>

                  <Section title="Payment method">
                    <div
                      style={{
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      <PayCard
                        selected={payMethod === "mpesa"}
                        onClick={() =>
                          setPayMethod("mpesa")
                        }
                        icon={<IconMpesa />}
                        label="M-Pesa"
                        desc="Approve the prompt on your phone"
                      />

                      <PayCard
                        selected={payMethod === "card"}
                        onClick={() =>
                          setPayMethod("card")
                        }
                        icon={<IconCard />}
                        label="Debit / credit card"
                        desc="Visa, Mastercard"
                      />

                      <PayCard
                        selected={payMethod === "bank"}
                        onClick={() =>
                          setPayMethod("bank")
                        }
                        icon={<IconBank />}
                        label="Bank transfer / mobile money"
                        desc="Equity, KCB, Airtel Money, T-Kash"
                      />
                    </div>

                    {/* M-Pesa */}

                    {payMethod === "mpesa" && (
                      <div style={{ marginTop: 14 }}>
                        <label
                          htmlFor="co-mpesa"
                          style={labelStyle}
                        >
                          M-Pesa phone number
                        </label>

                        <input
                          id="co-mpesa"
                          type="tel"
                          inputMode="tel"
                          className={`co-input${
                            errors.mpesaPhone
                              ? " error"
                              : ""
                          }`}
                          placeholder="e.g. 0712 345 678"
                          value={mpesaPhone}
                          onChange={(e) =>
                            setMpesaPhone(
                              e.target.value
                            )
                          }
                        />

                        {errors.mpesaPhone && (
                          <FieldError
                            msg={errors.mpesaPhone}
                          />
                        )}

                        <div
                          style={{
                            fontSize: 12,
                            color: T.muted,
                            marginTop: 6,
                            lineHeight: 1.5,
                          }}
                        >
                          You'll get an M-Pesa prompt
                          on this number. Enter your PIN
                          to pay{" "}
                          {formatKsh(paymentTotal)}.
                        </div>
                      </div>
                    )}

                    {/* Card */}

                    {payMethod === "card" && (
                      <Note>
                        You'll be taken to a secure page
                        to enter your card details after
                        you place the order.
                      </Note>
                    )}

                    {/* Bank */}

                    {payMethod === "bank" && (
                      <Note>
                        You'll get the bank and
                        mobile-money payment details after
                        you place the order.
                      </Note>
                    )}
                  </Section>

                  {/* Error */}

                  {submitError && (
                    <div
                      role="alert"
                      style={{
                        background: "#fdecee",
                        border: `1px solid ${T.error}`,
                        color: T.error,
                        borderRadius: 3,
                        padding: "10px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      {submitError}
                    </div>
                  )}

                  {/* Place order */}

                  <PrimaryBtn
                    onClick={handlePlaceOrder}
                    loading={placing}
                  >
                    {placing
                      ? "Placing order…"
                      : `Place order · ${formatKsh(
                          paymentTotal
                        )}`}
                  </PrimaryBtn>
                </>
              )}

              {/* ── Step 3: Payment status ───────────────────────────────── */}

              {step === 3 && (
                <Section>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "28px 16px",
                    }}
                  >
                    {/* Waiting */}

                    {payState === "sent" && (
                      <>
                        <span
                          style={{
                            display: "inline-block",
                            width: 56,
                            height: 56,
                            margin: "0 auto 16px",
                            border: `4px solid ${T.border}`,
                            borderTopColor: T.black,
                            borderRadius: "50%",
                            animation:
                              "co_spin 0.8s linear infinite",
                          }}
                        />

                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 800,
                            marginBottom: 8,
                          }}
                        >
                          Check your phone
                        </div>

                        <div
                          style={{
                            fontSize: 13.5,
                            color: T.inkSoft,
                            lineHeight: 1.7,
                            maxWidth: 360,
                            margin: "0 auto 4px",
                          }}
                        >
                          We sent an M-Pesa prompt to{" "}
                          <strong>
                            {mpesaPhone}
                          </strong>
                          . Enter your PIN to pay{" "}
                          {formatKsh(paymentTotal)}.
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            color: T.muted,
                            marginBottom: 4,
                          }}
                        >
                          This page updates on its own —
                          no need to refresh.
                        </div>
                      </>
                    )}

                    {/* Paid */}

                    {payState === "paid" && (
                      <>
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: T.yellow,
                            border: `3px solid ${T.black}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 16px",
                          }}
                        >
                          <IconCheck
                            width={26}
                            height={26}
                            style={{
                              color: T.black,
                            }}
                          />
                        </div>

                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 800,
                            marginBottom: 8,
                          }}
                        >
                          Payment received
                        </div>

                        <div
                          style={{
                            fontSize: 13.5,
                            color: T.inkSoft,
                            lineHeight: 1.7,
                            maxWidth: 360,
                            margin: "0 auto 18px",
                          }}
                        >
                          Thank you,{" "}
                          <strong>
                            {shipping.fullName
                              .trim()
                              .split(" ")[0] ||
                              "friend"}
                          </strong>
                          .

                          {result.ref && (
                            <>
                              {" "}
                              Your order{" "}
                              <strong>
                                #{result.ref}
                              </strong>{" "}
                              is confirmed and on its
                              way.
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {/* Failed */}

                    {payState === "failed" && (
                      <>
                        <div
                          style={{
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            background: "#fdecee",
                            border: `3px solid ${T.error}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: T.error,
                            margin: "0 auto 16px",
                          }}
                        >
                          <IconCross />
                        </div>

                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 800,
                            marginBottom: 8,
                          }}
                        >
                          Payment not completed
                        </div>
                      </>
                    )}

                    {/* Failed message */}

                    {submitError &&
                      payState === "failed" && (
                        <div
                          role="alert"
                          style={{
                            fontSize: 13,
                            color: T.error,
                            lineHeight: 1.6,
                            maxWidth: 360,
                            margin: "0 auto 18px",
                          }}
                        >
                          {submitError}
                        </div>
                      )}

                    {/* Order information */}

                    {payState !== "sent" && (
                      <div
                        style={{
                          background: T.surface,
                          border: `1px solid ${T.border}`,
                          borderRadius: 4,
                          padding: "12px 16px",
                          textAlign: "left",
                          fontSize: 13,
                          color: T.inkSoft,
                          marginBottom: 20,
                        }}
                      >
                        {result.ref && (
                          <Row
                            label="Order number"
                            value={`#${result.ref}`}
                            bold
                          />
                        )}

                        <Row
                          label="Deliver to"
                          value={`${shipping.town}, ${shipping.county}`}
                        />

                        <Row
                          label="Payment"
                          value={`M-Pesa · ${mpesaPhone}`}
                        />

                        <Row
                          label="Total"
                          value={formatKsh(
                            paymentTotal
                          )}
                          bold
                        />
                      </div>
                    )}

                    {/* Success button */}

                    {payState === "paid" && (
                      <button
                        type="button"
                        onClick={closeNow}
                        style={{
                          padding: "12px 32px",
                          background: T.black,
                          color: T.yellow,
                          border: "none",
                          borderRadius: 3,
                          fontWeight: 800,
                          fontSize: 14,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Continue shopping
                      </button>
                    )}

                    {/* Retry */}

                    {payState === "failed" && (
                      <button
                        type="button"
                        onClick={() => {
                          setStep(2);
                          setPayState(null);
                          setSubmitError("");

                          window.scrollTo({
                            top: 0,
                            behavior: "smooth",
                          });
                        }}
                        style={{
                          padding: "12px 32px",
                          background: T.black,
                          color: T.yellow,
                          border: "none",
                          borderRadius: 3,
                          fontWeight: 800,
                          fontSize: 14,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Try again
                      </button>
                    )}
                  </div>
                </Section>
              )}
            </div>

            {/* RIGHT: Order summary */}

            {step < 3 && (
              <div className="co-right-column">
                <OrderSummary
                  items={items}
                  productTotal={productTotal}
                  shippingFee={SHIPPING_FEE}
                  paymentTotal={paymentTotal}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ── Shared styles ───────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: 12.5,
  fontWeight: 700,
  color: T.inkSoft,
  display: "block",
  marginBottom: 5,
};

const linkButton = {
  background: "none",
  border: "none",
  padding: 0,
  fontSize: 12.5,
  color: T.ink,
  cursor: "pointer",
  fontWeight: 700,
  textDecoration: "underline",
  fontFamily: "inherit",
};

// ── Step bar ────────────────────────────────────────────────────────────────

function StepBar({ current }) {
  const allDone = current === 3;

  return (
    <div
      className="co-stepbar"
      style={{
        background: T.white,
        borderBottom: `1px solid ${T.border}`,
        padding: "12px 24px",
      }}
    >
      <div
        className="co-stepbar-inner"
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          maxWidth: 940,
          margin: "0 auto",
        }}
      >
        {STEPS.map((label, i) => {
          const done = i < current || allDone;
          const active =
            i === current && !allDone;
          const last = i === STEPS.length - 1;

          return (
            <React.Fragment key={label}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: done
                      ? T.black
                      : active
                      ? T.yellow
                      : "#e5e5e0",
                    color: done
                      ? T.yellow
                      : active
                      ? T.black
                      : T.muted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 800,
                    border: active
                      ? `2px solid ${T.black}`
                      : "none",
                    flexShrink: 0,
                  }}
                >
                  {done ? (
                    <IconCheck
                      width={12}
                      height={12}
                    />
                  ) : (
                    i + 1
                  )}
                </div>

                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: active ? 800 : 500,
                    color:
                      active || done
                        ? T.ink
                        : T.muted,
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
              </div>

              {!last && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    marginBottom: 14,
                    marginLeft: 4,
                    marginRight: 4,
                    background:
                      i < current || allDone
                        ? T.black
                        : "#e5e5e0",
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

function Section({
  title,
  action,
  children,
}) {
  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      {title && (
        <div
          style={{
            padding: "11px 16px",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#fafaf8",
          }}
        >
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 800,
            }}
          >
            {title}
          </span>

          {action}
        </div>
      )}

      <div
        style={{
          padding: "14px 16px",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ── Shipping form ───────────────────────────────────────────────────────────

const FIELDS = [
  {
    key: "fullName",
    label: "Full name",
    placeholder: "John Kamau",
    span: 2,
    autoComplete: "name",
  },
  {
    key: "phone",
    label: "Phone number",
    placeholder: "0712 345 678",
    span: 1,
    autoComplete: "tel",
    type: "tel",
  },
  {
    key: "email",
    label: "Email (optional)",
    placeholder: "you@email.com",
    span: 1,
    autoComplete: "email",
    type: "email",
  },
  {
    key: "county",
    label: "County",
    placeholder: "e.g. Kisumu",
    span: 1,
    autoComplete: "address-level1",
  },
  {
    key: "town",
    label: "Town / area",
    placeholder: "e.g. Milimani",
    span: 1,
    autoComplete: "address-level2",
  },
  {
    key: "address",
    label: "Street address",
    placeholder: "Building, road, landmark",
    span: 2,
    autoComplete: "street-address",
  },
];

function ShippingForm({
  values,
  onChange,
  errors,
}) {
  return (
    <div
      className="co-grid"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "12px",
      }}
    >
      {FIELDS.map((f) => (
        <div
          key={f.key}
          style={{
            gridColumn:
              f.span === 2
                ? "1 / -1"
                : "auto",
          }}
        >
          <label
            htmlFor={`co-${f.key}`}
            style={labelStyle}
          >
            {f.label}
          </label>

          <input
            id={`co-${f.key}`}
            type={f.type || "text"}
            autoComplete={f.autoComplete}
            className={`co-input${
              errors[f.key]
                ? " error"
                : ""
            }`}
            placeholder={f.placeholder}
            value={values[f.key]}
            onChange={(e) =>
              onChange(
                f.key,
                e.target.value
              )
            }
          />

          {errors[f.key] && (
            <FieldError
              msg={errors[f.key]}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ── Shipping summary ────────────────────────────────────────────────────────

function ShippingSummary({ data }) {
  return (
    <div
      style={{
        fontSize: 13.5,
        color: T.inkSoft,
        lineHeight: 1.8,
      }}
    >
      <div
        style={{
          fontWeight: 800,
          color: T.ink,
        }}
      >
        {data.fullName}
      </div>

      <div>
        {data.phone}
        {data.email
          ? ` · ${data.email}`
          : ""}
      </div>

      <div>
        {data.address}, {data.town},{" "}
        {data.county}
      </div>
    </div>
  );
}

// ── Payment card ────────────────────────────────────────────────────────────

function PayCard({
  selected,
  onClick,
  icon,
  label,
  desc,
}) {
  return (
    <button
      type="button"
      className={`co-pay-card${
        selected ? " selected" : ""
      }`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          border: `2px solid ${T.black}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {selected && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: T.black,
            }}
          />
        )}
      </span>

      <span
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        {icon}
      </span>

      <span
        style={{
          flex: 1,
        }}
      >
        <span
          style={{
            display: "block",
            fontSize: 13.5,
            fontWeight: 800,
            color: T.ink,
          }}
        >
          {label}
        </span>

        <span
          style={{
            display: "block",
            fontSize: 12,
            color: T.muted,
          }}
        >
          {desc}
        </span>
      </span>
    </button>
  );
}

// ── Note ────────────────────────────────────────────────────────────────────

function Note({ children }) {
  return (
    <div
      style={{
        marginTop: 14,
        padding: "12px 14px",
        background: "#fffde0",
        border: `1px solid ${T.black}`,
        borderRadius: 4,
        fontSize: 12.5,
        color: T.ink,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// ── Order summary ───────────────────────────────────────────────────────────

function OrderSummary({
  items,
  productTotal,
  shippingFee,
  paymentTotal,
}) {
  const count = items.reduce(
    (n, i) => n + i.qty,
    0
  );

  return (
    <div
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: 4,
        overflow: "hidden",
        fontSize: 13,
      }}
    >
      {/* Header */}

      <div
        style={{
          padding: "11px 14px",
          borderBottom: `1px solid ${T.border}`,
          background: "#fafaf8",
        }}
      >
        <span
          style={{
            fontWeight: 800,
          }}
        >
          Your order
        </span>

        <span
          style={{
            color: T.muted,
            marginLeft: 6,
          }}
        >
          ({count}{" "}
          {count === 1 ? "item" : "items"})
        </span>
      </div>

      {/* Items */}

      <div
        style={{
          padding: "4px 0",
        }}
      >
        {items.map((item) => (
          <ItemRow
            key={item.cartItemId}
            item={item}
          />
        ))}
      </div>

      {/* Totals */}

      <div
        style={{
          padding: "12px 14px",
          display: "grid",
          gap: 8,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            color: T.inkSoft,
          }}
        >
          <span>Items</span>

          <span
            style={{
              fontWeight: 700,
              color: T.ink,
            }}
          >
            {formatKsh(productTotal)}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            color: T.inkSoft,
          }}
        >
          <span>Delivery</span>

          <span
            style={{
              fontWeight: 700,
              color: T.ink,
            }}
          >
            {formatKsh(shippingFee)}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: `1px dashed ${T.border}`,
            paddingTop: 10,
            fontSize: 15,
            fontWeight: 800,
          }}
        >
          <span>Total</span>

          <span>
            {formatKsh(paymentTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Item row ────────────────────────────────────────────────────────────────

function ItemRow({ item }) {
  const variant = [
    item.selectedColor,
    item.selectedSize,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        padding: "10px 14px",
        borderBottom: `1px solid ${T.borderSoft}`,
      }}
    >
      {/* Image */}

      <div
        style={{
          width: 52,
          height: 52,
          flexShrink: 0,
          border: `1px solid ${T.borderSoft}`,
          borderRadius: 6,
          background: "#fafaf8",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.image_url ? (
          <img
            src={item.image_url}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <span
            style={{
              fontSize: 10,
              color: T.muted,
            }}
          >
            No image
          </span>
        )}
      </div>

      {/* Details */}

      <div
        style={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.title}
        </div>

        {variant && (
          <div
            style={{
              fontSize: 11.5,
              color: T.muted,
              marginTop: 2,
            }}
          >
            {variant}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: T.muted,
            }}
          >
            Qty {item.qty}
          </span>

          <span
            style={{
              fontSize: 13,
              fontWeight: 800,
            }}
          >
            {formatKsh(
              item.price * item.qty
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Primary button ──────────────────────────────────────────────────────────

function PrimaryBtn({
  onClick,
  children,
  loading,
}) {
  return (
    <button
      type="button"
      className="co-primary"
      onClick={onClick}
      disabled={loading}
      style={{
        width: "100%",
        padding: 14,
        background: T.black,
        color: T.yellow,
        border: "none",
        borderRadius: 3,
        fontWeight: 800,
        fontSize: 14.5,
        cursor: "pointer",
        fontFamily: "inherit",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
      }}
    >
      {loading && (
        <span
          style={{
            width: 16,
            height: 16,
            border:
              "2.5px solid rgba(255,240,0,.35)",
            borderTopColor: T.yellow,
            borderRadius: "50%",
            animation:
              "co_spin .7s linear infinite",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
      )}

      {children}
    </button>
  );
}

// ── Field error ─────────────────────────────────────────────────────────────

function FieldError({ msg }) {
  return (
    <div
      role="alert"
      style={{
        fontSize: 12,
        color: T.error,
        marginTop: 4,
        fontWeight: 600,
      }}
    >
      {msg}
    </div>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────

function Row({
  label,
  value,
  bold,
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 6,
      }}
    >
      <span
        style={{
          color: T.muted,
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontWeight: bold ? 800 : 500,
          color: T.ink,
          textAlign: "right",
        }}
      >
        {value}
      </span>
    </div>
  );
}